/**
 * Every tab of the contact detail page, in the order `Contact` declares its panels.
 *
 * `Tabs` addresses a panel by its position in the flattened panel list, so the
 * `contact-tabs` query parameter is an index and nothing else. Linking to that
 * number by hand breaks the moment a tab is inserted, which is why callers ask
 * for a tab by name through `contactTabPath` instead.
 *
 * The index counts the panels that are rendered, and two of these tabs are
 * dropped for a caller holding no rule for the commercial half of a contact —
 * so `contactTabPath` is told which ones are missing rather than assuming all
 * four. `useHiddenContactTabs` is the one answer to that question.
 *
 * A new tab is registered here at the position it is declared in `contact.tsx` —
 * `contactTabCyId` will not type-check until it is, and the position is what the
 * links resolve to.
 */
export const CONTACT_TAB_ORDER = [
	"general",
	"projects",
	"members",
	"services",
	"bank-accounts",
] as const;

export type ContactTab = (typeof CONTACT_TAB_ORDER)[number];

/**
 * Build the path to a contact with one of its tabs selected.
 *
 * @param contactId - The contact to open.
 * @param tab - The tab to select, by name.
 * @param hidden - The tabs the caller holds no rule for, from `useHiddenContactTabs`; they are not
 *   rendered, so they do not count towards the index either.
 * @returns A path including the `contact-tabs` index the named tab has for this caller.
 */
export function contactTabPath(contactId: string, tab: ContactTab, hidden: ContactTab[] = []) {
	const visible = CONTACT_TAB_ORDER.filter((name) => !hidden.includes(name));
	return `/contacts/${contactId}?contact-tabs=${visible.indexOf(tab)}`;
}

/**
 * The `data-cy` id of a contact tab, so a panel and the registry above cannot
 * disagree about which tabs exist.
 *
 * @param tab - The tab to identify, by name.
 * @returns The tab's `data-cy` id.
 */
export function contactTabCyId(tab: ContactTab): `${ContactTab}-tab` {
	return `${tab}-tab`;
}
